import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';

import MUIDataTable from "mui-datatables";
import { 
    Box, Tooltip, Typography, Chip, IconButton, useTheme, useMediaQuery, 
    Card, Stack, Button 
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoice, faChevronRight, faMotorcycle, faStore } from '@fortawesome/free-solid-svg-icons';
import UserContext from '../../../navigation/context/userContext';

export default function OrdersDataTable(props) {
    const { userData } = useContext(UserContext);
    const [orders, setOrders] = useState(props.orders);
    
    // --- DETECTOR DE PANTALLA ---
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const history = useHistory();
    
    const redirect = (route) => {
        history.push(route);
    };

    // --- DICCIONARIO VISUAL DE ESTATUS ---
    const getStatusChip = (status) => {
        let bgcolor = '#f5f5f5';
        let textcolor = '#333';

        switch (status) {
            case 'Finalizado':
            case 'Entregado':
                bgcolor = '#e8f5e9'; textcolor = '#2e7d32';
                break;
            case 'Aprobado':
            case 'Enviado':
                bgcolor = 'rgba(0, 118, 255, 0.08)'; textcolor = '#0076ff'; // Azul QUD minimalista
                break;
            case 'Solicitado':
            case 'Por enviar':
                bgcolor = '#fff3e0'; textcolor = '#ed6c02';
                break;
            case 'Rechazado':
            case 'Cancelado':
                bgcolor = '#ffebee'; textcolor = '#c62828';
                break;
            default:
                bgcolor = '#f5f5f5'; textcolor = '#666';
        }

        return (
            <Chip 
                label={status} 
                size="small" 
                sx={{ bgcolor, color: textcolor, fontWeight: '700', borderRadius: 1.5, px: 0.5 }} 
            />
        );
    };

    const columns = [
        {
            name: 'codigo',
            label: 'Código',
            options: {
                filter: false,
                customBodyRender: (value) => (
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#111' }}>
                        #{value}
                    </Typography>
                )
            }
        },
        {
            name: 'cliente',
            label: 'Cliente',
            options: {
                filter: false,
                customBodyRenderLite: (dataIndex) => (
                    <Box>
                        <Typography variant="subtitle2" fontWeight="700" color="#222">
                            {orders[dataIndex].cliente}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {orders[dataIndex].created_at}
                        </Typography>
                    </Box>
                )
            }
        },
        {
            name: 'tipo_entrega',
            label: 'Modalidad',
            options: {
                filter: true,
                customBodyRender: (value) => value ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <FontAwesomeIcon icon={value.toLowerCase().includes('delivery') ? faMotorcycle : faStore} color="#888" size="sm" />
                        <Typography variant="body2" color="text.secondary" fontWeight="500">{value}</Typography>
                    </Stack>
                ) : '-'
            }
        },
        {
            name: 'monto',
            label: 'Monto',
            options: {
                filter: false,
                customBodyRenderLite: (dataIndex) => (
                    <Typography variant="subtitle2" fontWeight="800" color="primary.main">
                        ${orders[dataIndex].monto}
                    </Typography>
                )
            }
        },
        {
            name: 'estatus',
            label: 'Estatus',
            options: {
                filter: true,
                customBodyRender: (value) => getStatusChip(value)
            }
        },
        {
            name: 'acciones',
            label: '',
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex) => (
                    <Box display="flex" justifyContent="flex-end">
                        <Tooltip title='Ver Detalles'>
                            <IconButton 
                                onClick={() => redirect('/orders/details/' + orders[dataIndex].codigo)}
                                sx={{ color: '#888', '&:hover': { color: 'primary.main', bgcolor: 'transparent' } }}
                            >
                                <FontAwesomeIcon icon={faChevronRight} size="sm" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }
        }
    ];

    const options = {
        filterType: 'checkbox',
        selectableRows: 'none',
        elevation: 0,
        searchPlaceholder: 'Buscar...',
        textLabels: {
            body: { noMatch: "No hay pedidos aún", toolTip: "Ordenar" },
            pagination: { next: "Siguiente", previous: "Anterior", rowsPerPage: "Filas:", displayRows: "de" },
            toolbar: { search: "Buscar", filterTable: "Filtrar" }
        }
    };

    // --- VISTA MÓVIL (TARJETAS MINIMALISTAS) ---
    if (isMobile) {
        return (
            <Box>
                {orders.map((order, index) => (
                    <Card 
                        key={index} 
                        elevation={0} 
                        onClick={() => redirect('/orders/details/' + order.codigo)}
                        sx={{ 
                            mb: 2, 
                            border: '1px solid #f0f0f0', 
                            borderRadius: 4, 
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:active': { transform: 'scale(0.98)', bgcolor: '#fafafa' }
                        }}
                    >
                        {/* Cabecera de la Tarjeta */}
                        <Box sx={{ p: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', fontWeight: 800, color: '#111' }}>
                                #{order.codigo}
                            </Typography>
                            {getStatusChip(order.estatus)}
                        </Box>
                        
                        {/* Cuerpo de la Tarjeta */}
                        <Box sx={{ px: 2, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                            <Box>
                                <Typography variant="body1" fontWeight="700" color="#222" noWrap sx={{ maxWidth: 200 }}>
                                    {order.cliente}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                    {order.created_at}
                                </Typography>
                                <Stack direction="row" spacing={0.8} alignItems="center">
                                    <FontAwesomeIcon icon={order.tipo_entrega?.toLowerCase().includes('delivery') ? faMotorcycle : faStore} color="#999" size="xs" />
                                    <Typography variant="caption" color="text.secondary" fontWeight="500">
                                        {order.tipo_entrega || 'Sin definir'}
                                    </Typography>
                                </Stack>
                            </Box>
                            
                            <Box textAlign="right">
                                <Typography variant="caption" color="text.secondary" display="block">Total</Typography>
                                <Typography variant="h6" fontWeight="800" color="primary.main" lineHeight={1}>
                                    ${order.monto}
                                </Typography>
                            </Box>
                        </Box>
                    </Card>
                ))}
            </Box>
        );
    }

    // --- VISTA ESCRITORIO (TABLA LIMPIA) ---
    return (
        <Box sx={{ border: '1px solid #f0f0f0', borderRadius: 4, overflow: 'hidden', bgcolor: '#fff' }}>
            <MUIDataTable
                data={orders}
                columns={columns}
                options={options}
            />
        </Box>
    );
}